import React, { memo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Tooltip, Spin, Button, message } from 'antd';
import {
  CloudServerOutlined,
  SecurityScanOutlined,
  DatabaseOutlined,
  CustomerServiceOutlined,
  LaptopOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
  LoadingOutlined,
  SyncOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

/**
 * Platform Status Bar
 *
 * Displays the configuration status of each MCP platform for a customer.
 * Shows which platforms have IDs mapped for the selected CIPP tenant.
 *
 * `m365Managed={false}` is a customer whose Microsoft 365 tenant CSG does not manage (no CIPP
 * id, spec 2026-10-19): M365 reads "Not managed" in a neutral style, is left out of the
 * configured total, and Sync is hidden — sync works by matching the M365 tenant.
 */

const StatusBarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const StatusLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
`;

const PlatformList = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`;

const PlatformBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: ${({ $isConfigured, theme }) =>
    $isConfigured ? 'var(--odin-status-completed-bg)' : 'var(--odin-surface-hover)'};
  border: 1px solid ${({ $isConfigured }) =>
    $isConfigured ? 'var(--odin-status-completed-border)' : 'var(--odin-surface-hover)'};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ $isConfigured, theme }) =>
      $isConfigured ? theme.colors.success : theme.colors.accent};
    transform: translateY(-1px);
  }
`;

const PlatformIcon = styled.span`
  font-size: 16px;
  color: ${({ $color, $isConfigured }) =>
    $isConfigured ? $color : 'var(--odin-text-tertiary)'};
  display: flex;
  align-items: center;
`;

const PlatformName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ $isConfigured, theme }) =>
    $isConfigured ? theme.colors.text : theme.colors.textMuted};
  font-weight: ${({ $isConfigured }) => ($isConfigured ? 500 : 400)};
`;

const StatusIcon = styled.span`
  font-size: 12px;
  color: ${({ $isConfigured, theme }) =>
    $isConfigured ? theme.colors.success : theme.colors.error};
  opacity: ${({ $isConfigured }) => ($isConfigured ? 1 : 0.6)};
  display: flex;
  align-items: center;
`;

const NotManagedIcon = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  align-items: center;
`;

const SummaryText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.accent};
  margin-left: auto;
  white-space: nowrap;
`;

const NotMappedText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  font-style: italic;
`;

const SyncButton = styled(Button)`
  && {
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    height: 28px;
    padding: 0 12px;
    border-radius: ${({ theme }) => theme.borderRadius.sm};

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }
`;

const SyncStatusText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
`;

// Platform configuration
const PLATFORMS = [
  {
    key: 'cipp',
    name: 'M365',
    icon: CloudServerOutlined,
    color: 'var(--odin-accent)',
    description: 'Microsoft 365 / CIPP',
  },
  {
    key: 'sophos',
    name: 'Sophos',
    icon: SecurityScanOutlined,
    color: 'var(--odin-accent)',
    description: 'Sophos Endpoint Security',
  },
  {
    key: 'acronis',
    name: 'Acronis',
    icon: DatabaseOutlined,
    color: 'var(--odin-accent)',
    description: 'Acronis Backup',
  },
  {
    key: 'autotask',
    name: 'Autotask',
    icon: CustomerServiceOutlined,
    color: 'var(--odin-accent)',
    description: 'Autotask Service Desk',
  },
  {
    key: 'scalepad',
    name: 'ScalePad',
    icon: LaptopOutlined,
    color: 'var(--odin-accent)',
    description: 'ScalePad Asset Management',
  },
  {
    key: 'datto',
    name: 'Datto',
    icon: DatabaseOutlined,
    color: 'var(--odin-accent)',
    description: 'Datto BCDR Backup',
  },
];

const PlatformStatusBar = memo(({
  platforms = {},
  configuredCount = 0,
  totalPlatforms = 6,
  loading = false,
  isMapped = true,
  displayName = null,
  onSync = null,
  isSyncing = false,
  cippTenantId = null,
  syncError = null,
  canSync = false,
  m365Managed = true,
}) => {
  const showSync = canSync && m365Managed;
  const total = m365Managed ? totalPlatforms : totalPlatforms - 1;

  const handleSyncClick = () => {
    if (onSync && cippTenantId && displayName) {
      onSync({ cippTenantId, displayName });
    }
  };

  if (loading) {
    return (
      <StatusBarContainer>
        <StatusLabel>Platform Status:</StatusLabel>
        <Spin indicator={<LoadingOutlined spin />} size="small" />
        <StatusLabel>Loading...</StatusLabel>
      </StatusBarContainer>
    );
  }

  if (!isMapped && m365Managed) {
    return (
      <StatusBarContainer>
        <StatusLabel>Platforms:</StatusLabel>
        {canSync && (
          <SyncButton
            type="primary"
            onClick={handleSyncClick}
            loading={isSyncing}
            disabled={isSyncing || !onSync}
            icon={!isSyncing && <SyncOutlined />}
          >
            {isSyncing ? 'Syncing...' : 'Sync Platforms'}
          </SyncButton>
        )}
        <SyncStatusText>
          {isSyncing
            ? 'Discovering platform IDs...'
            : syncError
              ? `Sync failed: ${syncError}`
              : 'Not configured yet'}
        </SyncStatusText>
      </StatusBarContainer>
    );
  }

  return (
    <StatusBarContainer>
      <StatusLabel>Platforms:</StatusLabel>
      <PlatformList>
        {PLATFORMS.map((platform) => {
          const isConfigured = !!platforms[platform.key];
          const notManaged = platform.key === 'cipp' && !m365Managed;
          const IconComponent = platform.icon;
          const label = notManaged
            ? `${platform.description} - Not managed`
            : isConfigured
              ? `${platform.description} - Configured`
              : `${platform.description} - Not configured`;

          return (
            <Tooltip key={platform.key} title={label}>
              <PlatformBadge $isConfigured={isConfigured} aria-label={label}>
                <PlatformIcon $color={platform.color} $isConfigured={isConfigured}>
                  <IconComponent />
                </PlatformIcon>
                <PlatformName $isConfigured={isConfigured}>
                  {platform.name}
                </PlatformName>
                {notManaged ? (
                  <NotManagedIcon>
                    <MinusCircleOutlined />
                  </NotManagedIcon>
                ) : (
                  <StatusIcon $isConfigured={isConfigured}>
                    {isConfigured ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                  </StatusIcon>
                )}
              </PlatformBadge>
            </Tooltip>
          );
        })}
      </PlatformList>
      <SummaryText>
        {configuredCount}/{total} configured
      </SummaryText>
      {showSync && (
        <Tooltip title="Re-discover customer mappings across all platforms">
          <SyncButton
            type="primary"
            onClick={handleSyncClick}
            loading={isSyncing}
            disabled={isSyncing || !onSync}
            icon={!isSyncing && <ReloadOutlined />}
            size="small"
          >
            {isSyncing ? 'Syncing...' : 'Re-Sync'}
          </SyncButton>
        </Tooltip>
      )}
    </StatusBarContainer>
  );
});

PlatformStatusBar.displayName = 'PlatformStatusBar';

PlatformStatusBar.propTypes = {
  platforms: PropTypes.shape({
    cipp: PropTypes.bool,
    sophos: PropTypes.bool,
    acronis: PropTypes.bool,
    autotask: PropTypes.bool,
    scalepad: PropTypes.bool,
    datto: PropTypes.bool,
  }),
  configuredCount: PropTypes.number,
  totalPlatforms: PropTypes.number,
  loading: PropTypes.bool,
  isMapped: PropTypes.bool,
  displayName: PropTypes.string,
  onSync: PropTypes.func,
  isSyncing: PropTypes.bool,
  cippTenantId: PropTypes.string,
  syncError: PropTypes.string,
  canSync: PropTypes.bool,
  m365Managed: PropTypes.bool,
};

export default PlatformStatusBar;
